'use client';

import * as React from 'react';
import {
    PhoneInput,
    type CountryIso2,
    type CountrySelectorProps,
    type PhoneInputRefType,
} from 'react-international-phone';
import { RequiredMarker } from '@/components/required-fields-legend';
import { inputFieldVariants, type InputFieldVariantProps } from '@/components/ui/field-variants';
import { Label } from '@/components/ui/label';
import { useAllowedPhoneCountries } from '@/hooks/use-phone-input-config';
import { fieldDescribedByIds } from '@/lib/field-described-by';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/** ISO 3166-1 alpha-2 country code (lowercase, as used by react-international-phone). */
export type Country = CountryIso2;

export type CustomPhoneInputProps = {
    id: string;
    name: string;
    label: React.ReactNode;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    autoComplete?: string;
    errorMessage?: string;
    errorId: string;
    hintId?: string;
    hintText?: string;
    defaultCountry?: Country | Uppercase<Country>;
    className?: string;
    labelClassName?: string;
    paddingClassName?: string;
    /** When set, appends a visual asterisk after the label. */
    required?: boolean;
    /**
     * Dial codes (`+39`) the country selector may offer.
     * Omit it, or pass an empty list, to keep every prefix. Registration and profile pass the whitelist.
     */
    allowedPrefixes?: string | readonly string[];
} & InputFieldVariantProps;

const CustomPhoneInput = React.forwardRef<HTMLInputElement, CustomPhoneInputProps>(function CustomPhoneInput(
    {
        id,
        name,
        label,
        value,
        onChange,
        onBlur,
        disabled,
        readOnly,
        autoComplete = 'tel',
        errorMessage,
        errorId,
        hintId,
        hintText,
        className,
        labelClassName,
        variant = 'floating',
        paddingClassName = 'pb-4',
        required,
        allowedPrefixes,
    },
    ref
) {
    const { countries, defaultCountry } = useAllowedPhoneCountries(allowedPrefixes);
    const { t } = useTranslation('registrationPage');
    const hasError = Boolean(errorMessage);
    const describedBy = fieldDescribedByIds({ errorId, hasError, hintId });

    return (
        <div className={cn('flex w-full min-w-0 flex-col mb-4', className)}>
            <div className={cn('relative', paddingClassName)}>
                <div className="relative">
                    <div className={cn(inputFieldVariants({ variant }), 'phone-input w-full')}>
                        <PhoneInput
                            ref={ref as React.Ref<PhoneInputRefType>}
                            defaultCountry={defaultCountry}
                            countries={countries}
                            value={value}
                            onChange={onChange}
                            disabled={disabled}
                            forceDialCode={true}
                            name={name}
                            className="flex min-h-0 min-w-0 flex-1"
                            countrySelectorStyleProps={
                                {
                                    renderButtonWrapper: ({
                                        children,
                                        rootProps,
                                    }: Parameters<NonNullable<CountrySelectorProps['renderButtonWrapper']>>[0]) => (
                                        <button
                                            {...rootProps}
                                            type="button"
                                            disabled={disabled || readOnly}
                                            aria-label={t('fields.phone.countrySelectorAriaLabel')}
                                            className="react-international-phone-country-selector-button">
                                            {children}
                                        </button>
                                    ),
                                } as unknown as NonNullable<React.ComponentProps<typeof PhoneInput>['countrySelectorStyleProps']>
                            }
                            inputProps={{
                                id,
                                onBlur,
                                autoComplete,
                                readOnly,
                                required,
                                'aria-invalid': hasError,
                                'aria-describedby': describedBy,
                                placeholder: ' ',
                            }}
                            inputClassName={cn(
                                'body-copy-l min-w-0 flex-1 border-0 bg-transparent text-[var(--field-text-color)] shadow-none outline-none disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                        />
                    </div>
                    <Label htmlFor={id} variant={variant} data-error={hasError} className={labelClassName}>
                        {label}
                        {required ? <RequiredMarker /> : null}
                    </Label>
                    {hintId && hintText && <span id={hintId} className="sr-only">
                        {hintText}
                    </span>}
                </div>
                <p
                    id={errorId}
                    className={cn(
                        'body-copy-s absolute bottom-3 translate-y-full left-0 right-0 whitespace-nowrap text-destructive',
                        !hasError && 'text-transparent'
                    )}>
                    {hasError && errorMessage}
                </p>
            </div>
        </div>
    );
});

export { CustomPhoneInput };
